import { useMemo, useState } from 'react';
import { loadRntrcByPlateService, loadVehicleByPlateService } from '@/services';
import {
  DeepPartial,
  Flatten,
  FormRisk,
  FormTruckComposition,
  FormTruckCompositionOwner,
  TruckTypeModel,
} from '@/types';
import { useErrorHandler, useToast } from '@/hooks';
import { deepFind, deepMerge, useSetFormValue } from '@/utils';
import { useAppContext } from '@/context';
import { valueOrEmptyString } from '@/utils/string';

export const usePlateSearch = (form: FormRisk, truckTypes: TruckTypeModel[]) => {
  const [loadingPlate, setLoadingPlate] = useState<boolean[]>([]);
  const toast = useToast();
  const { dispatch, state } = useAppContext();
  const setFormValue = useSetFormValue(dispatch);

  const loadedPlates = useMemo(() => state.vehicleSearch.data, [state.vehicleSearch.data]);
  const setLoadedPlates = (data: DeepPartial<FormTruckComposition>[]) => {
    dispatch({ action: 'vehicle_search_update_data', payload: data });
  };

  const getEmptyOwner = (): DeepPartial<FormTruckCompositionOwner> => ({
    cellPhone: '',
    cpfCnpj: '',
    name: '',
    rntrc: '',
    rntrcUuid: '',
    rntrcCpfCnpj: '',
    rntrcName: '',
    rntrcOwnerIsTruckOwner: false,
    address: {
      complement: '',
      neighborhood: '',
      number: '',
      street: '',
      zipCode: '',
    },
  });

  const getEmptyVehicle = (): DeepPartial<FormTruckComposition> => ({
    brand: '',
    chassis: '',
    color: '',
    location: undefined,
    model: '',
    modelYear: '',
    monofactureYear: '',
    plateApi: false,
    renavam: '',
    owner: getEmptyOwner(),
  });

  const errorHandler = useErrorHandler((error) => {
    toast.add({ type: 'error', title: error.message });
  });

  const searchPlate = (
    plate: string,
    position: number,
    doneCallback?: (position: number, composition: FormTruckComposition) => void,
  ) => {
    if (plate.length !== 7) return;

    const { compositions } = form.truck;
    setLoadingPlate(() => Array.from(Array(compositions.length), (_, index) => index === position));

    loadVehicleByPlateService(plate)
      .then(async (data) => {
        const rntrc = await loadRntrcByPlateService(plate);
        const type = truckTypes.find(({ id }) => id === form.truck.typeId);
        data.typeId = type.subtypes.filter((subtype) => subtype.id === data.typeId).length > 0 ? data.typeId : '';

        setLoadedPlates(
          Array.from(Array(compositions.length), (_, index) =>
            index === position ? data : loadedPlates[index] ?? getEmptyVehicle(),
          ),
        );

        const isSuccess = Boolean(data.plate);
        toast.add({
          type: isSuccess ? 'success' : 'error',
          title: isSuccess
            ? 'Dados encontrados com sucesso!'
            : 'Não conseguimos encontrar os dados, tente novamente mais tarde ou preencha manualmente.',
        });

        const finalData = deepMerge<FormTruckComposition>(form.truck.compositions[position], data, {
          plate,
          owner: {
            rntrc: valueOrEmptyString(rntrc?.rntrc),
            rntrcName: valueOrEmptyString(rntrc?.name),
            rntrcCpfCnpj: valueOrEmptyString(rntrc?.document),
            rntrcUuid: valueOrEmptyString(rntrc.rntrcUuid),
          },
        });

        setFormValue(`truck.compositions.${position}`, finalData);
        doneCallback(position, finalData);
      })
      .catch((error) => {
        setFormValue(`truck.compositions.${position}`, getEmptyVehicle());

        errorHandler(error);
      })
      .finally(() => {
        setLoadingPlate((old) => old.map((item, index) => (index === position ? false : item)));
      });
  };

  const isLoadedVehicleField = (position: number, path: keyof Flatten<FormTruckComposition>): boolean => {
    if (!Boolean(loadedPlates[position])) return false;
    const vehicle = loadedPlates[position];

    return Boolean(deepFind(vehicle, path));
  };

  return { searchPlate, loadingPlate, isLoadedVehicleField };
};
